import { ComponentProps, ReactElement } from "react";

import { Box, BoxProps, Divider, Stack } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";

import { useMediaQuery } from "@/common/hooks/useMediaQuery";
import { useTranslate } from "@/common/hooks/useTranslate";
import Image from "@/components/Image/Image";
import { LIVE_EDITOR_ANIMATION } from "@/components/LiveEditorElements/liveEditorAnimation";
import { LiveEditorProgressBar } from "@/components/LiveEditorElements/LiveEditorProgressBar";
import { DashboardButton, DashboardIconButton } from "@/dashboard-design-system/DashboardButton";
import { ArrowUp, Reveal } from "@/icons/outlined";
import type { IconElement } from "@/icons/types";

type DashboardButtonElement = ReactElement<ComponentProps<typeof DashboardButton>>;
type DashboardIconButtonElement = ReactElement<ComponentProps<typeof DashboardIconButton>>;
export type EditorNavButtonElement = DashboardButtonElement | DashboardIconButtonElement;

export type EditorNavMobileAction = {
  ariaLabel: string;
  icon?: IconElement;
  label?: string;
  onClick: () => void | Promise<void>;
  disabled?: boolean;
  isLoading?: boolean;
  "data-test"?: string;
};

export type EditorNavDesktopProps = {
  leftButtons?: EditorNavButtonElement[];
  rightButtons?: EditorNavButtonElement[];
  rightNodes?: ReactElement[];
  showLogo?: boolean;
  onLogoClick?: () => void;
};

export type EditorNavMobileProps = {
  leftAction?: EditorNavMobileAction;
  rightAction?: EditorNavMobileAction;
  /** Handler for preview button click. If undefined, preview button is hidden. */
  onPreviewClick?: () => void;
};

export type EditorNavMobilePreviewProps = {
  isOpen: boolean;
  onBackToEditingClick: () => void;
};

export type EditorNavProps = Omit<BoxProps, "children"> & {
  progress: {
    currentStepIndex: number;
    totalNumberOfSteps: number;
    mode: "with-progress-bar" | "hide" | "only-progress-bar";
  };
  desktop?: EditorNavDesktopProps;
  mobile?: EditorNavMobileProps;
  mobilePreview?: EditorNavMobilePreviewProps;
  zIndex?: number;
  "data-test"?: string;
};

const MobilePreviewBottomBar = ({ onBackToEditingClick }: { onBackToEditingClick?: () => void }) => {
  const { t } = useTranslate();

  return (
    <Box
      data-test="mobile-preview-bottom-bar"
      sx={(theme) => ({
        width: "100%",
        backgroundColor: theme.palette.surface.neutral.white,
        borderTop: `1px solid ${theme.palette.border.neutral.quiet}`,
        paddingX: theme.spacing(4),
        paddingTop: theme.spacing(3),
        paddingBottom: `calc(${theme.spacing(3)} + env(safe-area-inset-bottom, 0px))`,
        boxShadow: theme.shadows[8],
      })}
    >
      <Stack alignItems="center">
        <DashboardButton
          onClick={onBackToEditingClick}
          variant="text"
          size="small"
          vibe="neutral"
          data-test="mobile-preview-bottom-bar-close"
          startIcon={<ArrowUp />}
          // Fix: On touch devices, :hover state persists after tap. Reset hover styles for non-hover devices.
          sx={(theme) => ({
            ["@media (hover: none)"]: {
              "&:hover": { backgroundColor: "transparent", color: "inherit" },
              "&:active": { backgroundColor: theme.palette.surface.neutral.quiet },
            },
          })}
        >
          {t("dashboard", "liveFormEditor.topBar.backToEditing")}
        </DashboardButton>
      </Stack>
    </Box>
  );
};

export const LiveEditorTopBar = ({
  progress,
  desktop,
  mobile,
  mobilePreview,
  zIndex,
  "data-test": dataTest,
  sx,
  ...rest
}: EditorNavProps) => {
  const { t } = useTranslate();
  const { isSmallScreen } = useMediaQuery();
  const isMobilePreviewVisible = Boolean(isSmallScreen && mobilePreview?.isOpen);

  if (progress.mode === "only-progress-bar") {
    return (
      <LiveEditorProgressBar
        currentStepIndex={progress.currentStepIndex}
        totalNumberOfSteps={progress.totalNumberOfSteps}
      />
    );
  }

  if (isSmallScreen && mobile) {
    const hasOnlyPreviewButton = !mobile.leftAction && !mobile.rightAction;

    // Fix: On touch devices, :hover state persists after tap (unknown reasons). Reset hover styles for non-hover devices.
    const touchFriendlyButtonSx: SxProps<Theme> = (theme) => ({
      paddingX: 0,
      borderRadius: 0,
      ["@media (hover: none)"]: {
        "&:hover": { backgroundColor: "transparent", color: "inherit" },
        "&:active": { backgroundColor: theme.palette.surface.neutral.quiet },
      },
    });

    const renderMobileAction = (action: EditorNavMobileAction, dataTestFallback: string) => {
      const commonProps = {
        onClick: action.onClick,
        variant: "text" as const,
        vibe: "neutral" as const,
        "data-test": action["data-test"] ?? dataTestFallback,
        disabled: action.disabled,
        isLoading: action.isLoading,
        sx: touchFriendlyButtonSx,
      };

      // Text button with optional icon
      if (action.label) {
        return (
          <DashboardButton {...commonProps} size="large" startIcon={action.icon} aria-label={action.ariaLabel}>
            {action.label}
          </DashboardButton>
        );
      }

      // Icon-only button (requires icon)
      if (action.icon) {
        return (
          <DashboardIconButton {...commonProps} aria-label={action.ariaLabel}>
            {action.icon}
          </DashboardIconButton>
        );
      }

      return null;
    };

    const mobileContent = (
      <Stack
        data-test={dataTest}
        sx={{
          borderBottom: (theme) => `1px solid ${theme.palette.border.neutral.quiet}`,
          backgroundColor: (theme) => theme.palette.surface.neutral.white,
          minHeight: 68,
          height: "100%",
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-around"
          divider={<Divider orientation="vertical" sx={{ height: 36 }} />}
          sx={{ backgroundColor: "white", flex: 1, "& > *:not(hr)": { flex: "1 1 0", justifyContent: "center" } }}
        >
          {/* Left Action */}
          {mobile.leftAction && renderMobileAction(mobile.leftAction, "editor-nav-mobile-left-action")}

          {/* Preview Button - only shown when onPreviewClick is provided */}
          {mobile.onPreviewClick &&
            (hasOnlyPreviewButton ? (
              <DashboardButton
                onClick={mobile.onPreviewClick}
                variant="text"
                vibe="neutral"
                size="small"
                startIcon={<Reveal />}
                data-test="editor-nav-mobile-preview"
                sx={touchFriendlyButtonSx}
              >
                {t("dashboard", "preview")}
              </DashboardButton>
            ) : (
              <DashboardIconButton
                onClick={mobile.onPreviewClick}
                variant="text"
                vibe="neutral"
                size="small"
                data-test="editor-nav-mobile-preview"
                sx={touchFriendlyButtonSx}
              >
                <Reveal />
              </DashboardIconButton>
            ))}

          {/* Right Action */}
          {mobile.rightAction && renderMobileAction(mobile.rightAction, "editor-nav-mobile-right-action")}
        </Stack>
      </Stack>
    );

    return (
      <Box
        sx={{
          position: isMobilePreviewVisible ? "fixed" : "relative",
          top: 0,
          left: 0,
          width: "100%",
          zIndex,
          transition: LIVE_EDITOR_ANIMATION.transition,
          transform: isMobilePreviewVisible ? "translateY(calc(100vh - 100%))" : "translateY(0)",
          ["@supports (height: 100dvh)"]: {
            transform: isMobilePreviewVisible ? "translateY(calc(100dvh - 100%))" : "translateY(0)",
          },
        }}
      >
        {isMobilePreviewVisible ? (
          <MobilePreviewBottomBar onBackToEditingClick={mobilePreview?.onBackToEditingClick} />
        ) : (
          mobileContent
        )}
      </Box>
    );
  }

  const baseSx: SxProps<Theme> = (theme) => ({
    position: "relative",
    backgroundColor: theme.palette.surface.neutral.white,
    borderBottom: `1px solid ${theme.palette.border.neutral.quiet}`,
  });
  const combinedSx = (sx ? [baseSx, sx] : baseSx) as SxProps<Theme>;

  return (
    <Box
      sx={{
        position: isMobilePreviewVisible ? "fixed" : "relative",
        top: 0,
        left: 0,
        width: "100%",
        zIndex,
        transition: LIVE_EDITOR_ANIMATION.transition,
        transform: isMobilePreviewVisible ? "translateY(calc(100vh - 100%))" : "translateY(0)",
      }}
    >
      {isMobilePreviewVisible ? (
        <MobilePreviewBottomBar onBackToEditingClick={mobilePreview?.onBackToEditingClick} />
      ) : (
        <Box {...rest} data-test={dataTest} sx={combinedSx}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={(theme) => ({
              paddingX: theme.spacing(6),
              paddingTop: theme.spacing(2),
              paddingBottom: theme.spacing(2),
              gap: theme.spacing(2),
              flexWrap: "wrap",
              minHeight: "36px",
              boxSizing: "content-box",
            })}
          >
            {(desktop?.showLogo || desktop?.leftButtons) && (
              <Stack
                direction="row"
                alignItems="center"
                sx={(theme) => ({
                  gap: theme.spacing(1),
                  flexWrap: "wrap",
                })}
              >
                {desktop?.showLogo && (
                  <Box
                    component={desktop?.onLogoClick ? "button" : "div"}
                    onClick={desktop?.onLogoClick}
                    sx={{
                      background: "none",
                      border: "none",
                      padding: 0,
                      cursor: desktop?.onLogoClick ? "pointer" : "default",
                      display: "flex",
                      alignItems: "center",
                    }}
                    aria-label={
                      desktop?.onLogoClick ? t("dashboard", "liveFormEditor.topBar.goToCampaigns") : undefined
                    }
                  >
                    <Image alt="Zeffy Icon" src="/assets/icons/zeffy.svg" height={24} width={80} />
                  </Box>
                )}
                {desktop?.leftButtons}
              </Stack>
            )}
            {desktop?.rightButtons && (
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="flex-end"
                sx={(theme) => ({
                  gap: theme.spacing(1),
                  flexWrap: "wrap",
                })}
              >
                {desktop?.rightNodes}
                {desktop?.rightButtons}
              </Stack>
            )}
          </Stack>
          {progress.mode === "with-progress-bar" && (
            <LiveEditorProgressBar
              currentStepIndex={progress.currentStepIndex}
              totalNumberOfSteps={progress.totalNumberOfSteps}
            />
          )}
        </Box>
      )}
    </Box>
  );
};
